/**
 * Authentication API Service
 * 
 * Handles login, logout, token refresh, registration, and password reset.
 */

import { httpClient, setAuthToken, clearAuthToken, getStoredUser, ApiError } from './HttpClient';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
      name: string;
      role: 'admin' | 'agent' | 'support' | 'staff' | 'end_user';
      agentId?: string;
      parentAccountId?: string;
      distributorLevel?: number;
    };
  };
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'agent' | 'support' | 'staff' | 'end_user';
  agentId?: string;
  parentAccountId?: string;
  distributorLevel?: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  agentId?: string;
  companyName?: string;
  location?: string;
  contactPhone?: string;
  code: string;
}

export interface RegisterResponse {
  success: boolean;
  message?: string;
  data: {
    message: string;
    user: AuthUser;
  };
}

interface MessageResponse {
  success: boolean;
  data: { message: string };
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  const response = await httpClient.post<LoginResponse>('/auth/login', { email, password }, { skipAuth: true });
  
  if (response.success && response.data) {
    setAuthToken(response.data.token, response.data.user);
    return response.data.user;
  }
  
  throw new ApiError(401, 'LOGIN_FAILED', 'Login failed');
}

/**
 * Send registration verification code to email
 */
export async function sendRegisterCode(email: string): Promise<string> {
  const response = await httpClient.post<MessageResponse>(
    '/auth/register/send-code',
    { email },
    { skipAuth: true }
  );

  if (response.success && response.data) {
    return response.data.message;
  }

  throw new ApiError(400, 'SEND_CODE_FAILED', 'Failed to send verification code');
}

/**
 * Register a new merchant account (staff + merchant ID) with email verification
 */
export async function register(data: RegisterRequest): Promise<AuthUser> {
  const response = await httpClient.post<RegisterResponse>('/auth/register', data, { skipAuth: true });

  if (response.success && response.data) {
    return response.data.user;
  }

  throw new ApiError(400, 'REGISTER_FAILED', 'Registration failed');
}

/**
 * Send password reset verification code
 */
export async function sendForgotPasswordCode(email: string): Promise<string> {
  const response = await httpClient.post<MessageResponse>(
    '/auth/forgot-password/send-code',
    { email },
    { skipAuth: true }
  );

  if (response.success && response.data) {
    return response.data.message;
  }

  throw new ApiError(400, 'SEND_CODE_FAILED', 'Failed to send verification code');
}

/**
 * Reset password with email verification code
 */
export async function resetPassword(data: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<string> {
  const response = await httpClient.post<MessageResponse>(
    '/auth/forgot-password/reset',
    data,
    { skipAuth: true }
  );

  if (response.success && response.data) {
    return response.data.message;
  }

  throw new ApiError(400, 'RESET_FAILED', 'Password reset failed');
}

/**
 * Refresh auth token
 */
export async function refreshToken(): Promise<AuthUser> {
  const response = await httpClient.post<LoginResponse>('/auth/refresh');
  
  if (response.success && response.data) {
    setAuthToken(response.data.token, response.data.user);
    return response.data.user;
  }
  
  throw new ApiError(401, 'REFRESH_FAILED', 'Token refresh failed');
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  try {
    await httpClient.post('/auth/logout');
  } catch {
    // Ignore logout errors - clear local token anyway
  }
  clearAuthToken();
}

/**
 * Get current user from local storage
 */
export function getCurrentUser(): AuthUser | null {
  return getStoredUser();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}
